/**
 * Drag-and-drop file upload area with file list, progress indicators, and removal support.
 *
 * @module components/common/DragDropArea
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {LinearProgress, Tooltip} from "@material-ui/core";
import List from "@material-ui/core/List";
import Button from "@material-ui/core/Button";
import AddIcon from "@material-ui/icons/Add";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import CancelIcon from "@material-ui/icons/Cancel";
import { makeStyles } from '@material-ui/core/styles';
import DragDropAreaStyle from './DragDropArea';
import CircularProgress from "@material-ui/core/CircularProgress";
import {formatSizeInBytes} from "../../utils/i18n";

/**
 * DragDropArea is a React functional component that allows users to upload files
 * through drag-and-drop or file input selection. It maintains the state of the
 * selected files, displays a list of files with corresponding upload progress,
 * and provides options to remove files or retry if an upload error occurs.
 *
 * @param {Object} props - The properties object.
 * @param {function} props.onFilesSelected - Callback function triggered whenever
 * the list of selected files is updated.
 * @param {number} props.fileUploadProgress - The progress percentage of the current file upload.
 * @param {boolean} props.resetFiles - If set to true, clears all uploaded files from the state.
 */
export const DragDropArea = ({
    onFilesSelected,
    fileUploadProgress,
    resetFiles
}) => {
    const useStyles = makeStyles((theme) => DragDropAreaStyle(theme));
    const classes = useStyles();
    const inputFileRef = React.useRef();
    const [files, setFiles] = React.useState([]);
    const [uploadProgress, setUploadProgress] = React.useState([]);

    useEffect(() => {
        onFilesSelected(files);
    }, [files, onFilesSelected]);

    useEffect(
        () => {
            if (resetFiles) {
                setFiles([]);
            }
        },
        [resetFiles]
    );

    useEffect(
        () => {
            setUploadProgress(fileUploadProgress);
        },
        [fileUploadProgress]
    );

    /**
     * Updates the list of files by appending new files with their initial status and progress values.
     * The function takes an input file list, transforms it into an array of file objects with default
     * properties, and appends it to the existing file list.
     *
     * @param {FileList} fileList - A FileList object containing the files to be added. Each file in the list
     * will be converted to an object with initial status and progress values.
     */
    const updateFileList = (fileList) => {
        if (fileList && fileList.length > 0) {
            const newFiles = Array.from(fileList).map((f) => {
                return {
                    file: f,
                    status: "",
                    progress: -1
                }
            });
            setFiles([...files, ...newFiles]);
        }
    }

    /**
     * Handles the click event for adding an attachment.
     * Triggers a click event on the input file element referenced by `inputFileRef`.
     *
     * @param {Event} evt - The event object associated with the click action.
     */
    const handleAddAttachmentClick = (/* evt */) => {
        inputFileRef.current.click();
    }

    /**
     * Handles the drop event triggered when a user drops a file or files onto a designated drop zone.
     * Prevents the default browser behavior for drop events and updates the file list
     * with the files provided in the event's data transfer object.
     *
     * @param {DragEvent} evt - The drag event object containing details about the drop operation.
     */
    const handleDrop = (evt) => {
        evt.preventDefault();
        updateFileList(evt.dataTransfer.files);
    }

    /**
     * Removes a file from the current list of files at the specified index.
     *
     * This function updates the state by filtering out the file located
     * at the given index from the array of files. It is primarily used
     * for managing file removal within a component's state.
     *
     * @param {number} index - The index of the file to be removed from the list.
     */
    const handleRemoveFile = (index) => {
        setFiles(files.filter((_, i) => i !== index));
    }

    /**
     * Handles the drag over event during a drag-and-drop interaction.
     *
     * Prevents the default behavior of the drag over event to allow
     * drop operations to be performed.
     *
     * @param {DragEvent} evt - The drag event object representing the drag over action.
     */
    const handleDropOver = (evt) => {
        evt.preventDefault();
    }

    /**
     * Handles the change event for a file input element.
     * Prevents the default behavior of the event and updates the file list.
     *
     * @param {Event} evt - The event object representing the change event.
     * @param {EventTarget} evt.target - The target element from which the event originated.
     * @param {FileList} evt.target.files - The list of files selected in the file input element.
     */
    const handleFileChange = (evt) => {
        evt.preventDefault();
        updateFileList(evt.target.files);
    }

    return (
        <>
            <section className={`${classes.dragDrop}${files.length ? ' active' : ''}`}
                     onDrop={handleDrop}
                     onDragOver={handleDropOver}
            >
                <Tooltip title={"Drag and drop here any files you wish to attach to this ticket"}>
                    <List className={classes.fileList}>
                        {files?.length ? files.map((file, index) => (
                            <ListItem className={classes.fileItem} key={index}>
                                <ListItemIcon></ListItemIcon>
                                <ListItemText className={classes.fileInfo}>
                                    <p>{file?.file?.name ?? ''}</p>
                                    <p>{file?.file?.type ?? ''}</p>
                                    <p>{formatSizeInBytes(file?.file?.size ?? 0)}</p>
                                </ListItemText>
                                <ListItemSecondaryAction className={classes.fileActions}>
                                    {file?.status === 'uploading' ? (
                                        <CircularProgress />
                                    ) : (
                                        file?.status === 'error' ? (
                                            <Tooltip title="Could not upload the attachment">
                                                <IconButton onClick={() => handleRemoveFile(index)}>
                                                    <CancelIcon />
                                                </IconButton>
                                            </Tooltip>
                                            ) : (
                                                <IconButton onClick={() => handleRemoveFile(index)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            )
                                    )}
                                </ListItemSecondaryAction>
                            </ListItem>
                        )) : (<></>)}
                    </List>
                </Tooltip>
                <LinearProgress value={uploadProgress} hidden={!uploadProgress}/>
            </section>
            <input
                type="file"
                hidden
                id="browse"
                ref={inputFileRef}
                onChange={handleFileChange}
                accept=""
                multiple
                title=""
                value=""
            />
            <Button onClick={handleAddAttachmentClick}>
                <AddIcon />
                Add attachment
            </Button>
        </>
    )
}

DragDropArea.propTypes = {
    onFilesSelected: PropTypes.func,
    attachments: PropTypes.array,
    fileUploadProgress: PropTypes.number,
    resetFiles: PropTypes.bool,
};

export default DragDropArea;
